import React, { useEffect, useState } from 'react';
import { api } from '@/contexts/AuthContext';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Mail, Save, Zap, BellRing, Send, AlertCircle, Plus, Trash2, ToggleLeft, PhoneCall, Award } from 'lucide-react';
import { toast } from 'sonner';

import { WasabiClientsCard } from '@/components/settings/WasabiClientsCard';
import { GeocodingStatusCard } from '@/components/settings/GeocodingStatusCard';
import { CompetitorSyncCard } from '@/components/settings/CompetitorSyncCard';
import { HanagroupAuditCard } from '@/components/settings/HanagroupAuditCard';
import { WeeklyReportCard } from '@/components/settings/WeeklyReportCard';
import { BackOfficeEnrichmentCard } from '@/components/settings/BackOfficeEnrichmentCard';
import { NotificationCenterCard } from '@/components/settings/NotificationCenterCard';
import { DataRecoveryCard } from '@/components/settings/DataRecoveryCard';
import { EnrichmentApisKillSwitchCard } from '@/components/settings/EnrichmentApisKillSwitchCard';
import { CommercialsTerritoriesCard } from '@/components/settings/CommercialsTerritoriesCard';
import { SystemeUImportCard } from '@/components/settings/SystemeUImportCard';
import { ProspectionTemplateCard } from '@/components/settings/ProspectionTemplateCard';

const ENGINE_SETTINGS = {
  seuil_score_mini: { label: 'Seuil score minimum (%)', type: 'number' },
  format_telephone: { label: 'Format téléphone', type: 'select', options: ['FR', 'International'] },
  nb_tentatives_recherche: { label: 'Nombre de tentatives de recherche', type: 'number' },
  timeout_recherche: { label: 'Timeout recherche (secondes)', type: 'number' },
  enrichment_mode: { label: "Mode d'enrichissement", type: 'select', options: ['mock', 'serpapi'] },
};

const EMAIL_SETTINGS = {
  email_intermarche: { label: 'Pattern Intermarché', placeholder: '{prenom}.{nom}-adh@mousquetaires.com' },
  email_systeme_u: { label: 'Pattern Système U', placeholder: '{prenom}.{nom}@cooperative-u.fr' },
  email_leclerc: { label: 'Pattern Leclerc', placeholder: '{prenom}.{nom}@{sca}.leclerc' },
};

const RDV_VARS = [
  'enseigne', 'marque', 'nom_magasin', 'rue', 'code_postal', 'ville',
  'telephone', 'prenom_dirigeant', 'nom_dirigeant', 'email_pro',
  'commercial', 'commercial_email', 'date_rdv', 'rdv_date', 'rdv_heure', 'rdv_datetime', 'notes',
];

export default function SettingsPage() {
  const { user } = useAuth();
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  const [recipientsList, setRecipientsList] = useState([]);

  // Parse the recipients JSON/text on load
  const parseRecipients = (raw) => {
    if (!raw) return [];
    try {
      if (raw.trim().startsWith('[')) return JSON.parse(raw);
    } catch {}
    // Fallback: parse "Name <email>" or bare email
    return raw.split(/[,;\n]+/).map(s => s.trim()).filter(Boolean).map(p => {
      const m = p.match(/^(.+?)\s*<([^>]+)>$/);
      if (m) return { label: m[1].trim(), email: m[2].trim() };
      return { label: p, email: p };
    });
  };

  useEffect(() => {
    api.get('/settings').then(r => {
      setSettings(r.data);
      setRecipientsList(parseRecipients(r.data.rdv_notif_recipients));
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const update = (key, value) => setSettings(s => ({ ...s, [key]: value }));

  const updateRecipient = (idx, field, value) => {
    setRecipientsList(list => list.map((r, i) => i === idx ? { ...r, [field]: value } : r));
  };
  const addRecipient = () => setRecipientsList(list => [...list, { label: '', email: '' }]);
  const removeRecipient = (idx) => setRecipientsList(list => list.filter((_, i) => i !== idx));

  const handleSave = async () => {
    setSaving(true);
    try {
      const validRecipients = recipientsList
        .filter(r => r.email && r.email.includes('@'))
        .map(r => ({ label: (r.label || r.email).trim(), email: r.email.trim() }));
      const payload = { ...settings, rdv_notif_recipients: JSON.stringify(validRecipients) };
      const { data } = await api.put('/settings', { settings: payload });
      setSettings(data);
      setRecipientsList(parseRecipients(data.rdv_notif_recipients));
      toast.success('Paramètres enregistrés');
    } catch { toast.error('Erreur'); }
    setSaving(false);
  };

  const sendTest = async () => {
    if (!testEmail || !testEmail.includes('@')) { toast.error('Email invalide'); return; }
    setSendingTest(true);
    try {
      const { data } = await api.post('/notifications/test-rdv-email', { destinataire: testEmail });
      toast.success(data.message || 'Email de test envoyé');
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'envoi");
    }
    setSendingTest(false);
  };

  if (loading) return <div className="animate-pulse-subtle text-center py-20 text-[#71717A]">Chargement...</div>;

  const rdvEnabled = (settings.rdv_notif_enabled || 'true') === 'true';
  const modeAppelEnabled = (settings.feature_mode_appel_enabled || 'true') === 'true';
  const coachEnabled = (settings.feature_coach_du_jour_enabled || 'false') === 'true';
  const validRecipients = recipientsList.filter(r => r.email && r.email.includes('@'));
  const recipientsCount = validRecipients.length;

  return (
    <div className="space-y-6 animate-fade-in max-w-3xl" data-testid="settings-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>Paramètres</h1>
        <p className="text-sm text-[#71717A] mt-1">Configuration du moteur d'enrichissement, des emails et des notifications</p>
      </div>

      <NotificationCenterCard />
      <EnrichmentApisKillSwitchCard />
      <DataRecoveryCard />
      <WasabiClientsCard />
      <GeocodingStatusCard />
      <CompetitorSyncCard />
      {(user?.role === 'super_admin' || user?.role === 'admin') && <CommercialsTerritoriesCard />}
      {(user?.role === 'super_admin' || user?.role === 'admin') && <WeeklyReportCard />}
      {(user?.role === 'super_admin' || user?.role === 'admin') && <BackOfficeEnrichmentCard />}
      {(user?.role === 'super_admin' || user?.role === 'admin') && <HanagroupAuditCard />}
      <SystemeUImportCard />
      {(user?.role === 'super_admin' || user?.role === 'admin') && <ProspectionTemplateCard />}

      {/* === RDV NOTIFICATIONS === */}
      <Card className="border-2 border-[#F97316]/20 shadow-sm bg-gradient-to-br from-[#FFF7ED] to-white" data-testid="rdv-notif-card">
        <CardContent className="p-6 space-y-5">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-2">
              <BellRing className="w-5 h-5 text-[#F97316]" />
              <div>
                <h3 className="text-base font-semibold">Notifications RDV</h3>
                <p className="text-xs text-[#71717A] mt-0.5">Envoyer un email automatique à chaque RDV pris</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Label htmlFor="rdv-enabled" className="text-sm font-medium">{rdvEnabled ? 'Actif' : 'Désactivé'}</Label>
              <Switch
                id="rdv-enabled"
                checked={rdvEnabled}
                onCheckedChange={v => update('rdv_notif_enabled', v ? 'true' : 'false')}
                data-testid="rdv-notif-enabled-switch"
              />
            </div>
          </div>

          {rdvEnabled && recipientsCount === 0 && (
            <div className="p-3 rounded-lg bg-[#FEF2F2] border border-[#FCA5A5] text-sm flex items-start gap-2" data-testid="rdv-no-recipients-warning">
              <AlertCircle className="w-4 h-4 text-[#EF4444] mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-[#B91C1C]">Aucun destinataire configuré</p>
                <p className="text-[#991B1B] text-xs mt-0.5">Ajoute au moins une adresse email pour activer l'envoi automatique.</p>
              </div>
            </div>
          )}

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-sm">Destinataires configurés</Label>
              <Button type="button" onClick={addRecipient} size="sm" variant="outline" className="h-7 gap-1 text-xs" data-testid="rdv-add-recipient-btn">
                <Plus className="w-3 h-3" /> Ajouter
              </Button>
            </div>
            {recipientsList.length === 0 ? (
              <p className="text-xs text-[#71717A] italic py-3 text-center bg-white rounded-lg border border-dashed border-[#E5E7EB]">
                Aucun destinataire. Cliquez sur "Ajouter".
              </p>
            ) : (
              <div className="space-y-2" data-testid="rdv-recipients-list">
                {recipientsList.map((r, idx) => (
                  <div key={idx} className="flex items-center gap-2 bg-white p-2 rounded-lg border border-[#E5E7EB]">
                    <Input
                      value={r.label || ''}
                      onChange={e => updateRecipient(idx, 'label', e.target.value)}
                      placeholder="Nom (ex: Direction)"
                      className="h-8 text-sm flex-1"
                      data-testid={`rdv-recipient-label-${idx}`}
                    />
                    <Input
                      value={r.email || ''}
                      onChange={e => updateRecipient(idx, 'email', e.target.value)}
                      placeholder="email@wasabicorner.fr"
                      className="h-8 text-sm flex-[2] font-mono"
                      data-testid={`rdv-recipient-email-${idx}`}
                    />
                    <Button
                      type="button"
                      onClick={() => removeRecipient(idx)}
                      size="sm"
                      variant="ghost"
                      className="h-8 w-8 p-0 text-[#EF4444] hover:bg-[#FEF2F2]"
                      data-testid={`rdv-recipient-remove-${idx}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
            {recipientsCount > 0 && <p className="text-xs text-[#10B981] mt-2">{recipientsCount} destinataire(s) valide(s) — les commerciaux pourront choisir lesquels notifier lors d'un RDV.</p>}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm mb-1.5 block">Expéditeur (From)</Label>
              <Input
                value={settings.rdv_notif_from || ''}
                onChange={e => update('rdv_notif_from', e.target.value)}
                placeholder="notifications@wasabicorner.fr"
                className="h-9 bg-white text-sm font-mono"
                data-testid="rdv-notif-from"
              />
              <p className="text-xs text-[#71717A] mt-1">Nécessite un domaine vérifié dans Resend</p>
            </div>
            <div>
              <Label className="text-sm mb-1.5 block">Copie (CC) <span className="text-[#71717A] text-xs font-normal">optionnel</span></Label>
              <Input
                value={settings.rdv_notif_cc || ''}
                onChange={e => update('rdv_notif_cc', e.target.value)}
                placeholder="manager@wasabicorner.fr"
                className="h-9 bg-white text-sm font-mono"
                data-testid="rdv-notif-cc"
              />
            </div>
          </div>

          <div className="flex items-center justify-between gap-3 p-3 bg-white rounded-lg border border-[#E5E7EB]">
            <div className="flex-1">
              <Label htmlFor="rdv-attach-pdf" className="text-sm font-medium cursor-pointer">Joindre la fiche PDF du prospect</Label>
              <p className="text-xs text-[#71717A] mt-0.5">Le destinataire reçoit la fiche complète (timeline, dirigeant, coordonnées) en pièce jointe</p>
            </div>
            <Switch
              id="rdv-attach-pdf"
              checked={(settings.rdv_notif_attach_pdf || 'true') === 'true'}
              onCheckedChange={v => update('rdv_notif_attach_pdf', v ? 'true' : 'false')}
              data-testid="rdv-notif-attach-pdf-switch"
            />
          </div>

          <div>
            <Label className="text-sm mb-1.5 block">Objet du mail</Label>
            <Input
              value={settings.rdv_notif_subject || ''}
              onChange={e => update('rdv_notif_subject', e.target.value)}
              placeholder="Nouveau RDV pris : {enseigne} - {ville}"
              className="h-9 bg-white text-sm"
              data-testid="rdv-notif-subject"
            />
          </div>

          <div>
            <Label className="text-sm mb-1.5 block">Corps du mail (HTML)</Label>
            <Textarea
              value={settings.rdv_notif_body || ''}
              onChange={e => update('rdv_notif_body', e.target.value)}
              rows={10}
              className="bg-white text-xs font-mono"
              data-testid="rdv-notif-body"
            />
            <div className="mt-2 p-3 bg-[#F9FAFB] rounded-lg">
              <p className="text-xs font-semibold text-[#71717A] mb-2">Variables disponibles :</p>
              <div className="flex flex-wrap gap-1.5">
                {RDV_VARS.map(v => (
                  <code key={v} className="text-xs bg-white border border-[#E5E7EB] px-1.5 py-0.5 rounded text-[#F97316]">
                    {`{${v}}`}
                  </code>
                ))}
              </div>
            </div>
          </div>

          {/* Test Email */}
          <Separator />
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <Label className="text-sm mb-1.5 block">Tester la configuration</Label>
              <Input
                type="email"
                value={testEmail}
                onChange={e => setTestEmail(e.target.value)}
                placeholder="votre@email.fr"
                className="h-9 bg-white text-sm"
                data-testid="rdv-test-email-input"
              />
            </div>
            <Button
              onClick={sendTest}
              disabled={sendingTest || !testEmail}
              variant="outline"
              className="h-9 gap-1.5 border-[#F97316] text-[#F97316] hover:bg-[#FFF7ED]"
              data-testid="rdv-send-test-btn"
            >
              <Send className="w-3.5 h-3.5" /> {sendingTest ? 'Envoi...' : 'Envoyer test'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* === FEATURES === */}
      <Card className="border border-[#E5E7EB] shadow-sm" data-testid="features-card">
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center gap-2 mb-2">
            <ToggleLeft className="w-4 h-4 text-[#0055FF]" />
            <h3 className="text-sm font-semibold">Fonctionnalités</h3>
          </div>
          <div className="flex items-center justify-between gap-3 p-3 bg-white rounded-lg border border-[#E5E7EB]">
            <div className="flex items-start gap-3 flex-1">
              <PhoneCall className="w-4 h-4 text-[#0055FF] mt-0.5" />
              <div>
                <Label htmlFor="feat-mode-appel" className="text-sm font-medium cursor-pointer">Mode Appel</Label>
                <p className="text-xs text-[#71717A] mt-0.5">Affiche le lien "Mode Appel" dans la sidebar pour lancer des sessions d'appels back-to-back.</p>
              </div>
            </div>
            <Switch
              id="feat-mode-appel"
              checked={modeAppelEnabled}
              onCheckedChange={v => update('feature_mode_appel_enabled', v ? 'true' : 'false')}
              data-testid="feat-mode-appel-switch"
            />
          </div>
          <div className="flex items-center justify-between gap-3 p-3 bg-white rounded-lg border border-[#E5E7EB]">
            <div className="flex items-start gap-3 flex-1">
              <Award className="w-4 h-4 text-[#10B981] mt-0.5" />
              <div>
                <Label htmlFor="feat-coach-jour" className="text-sm font-medium cursor-pointer">Coach du jour</Label>
                <p className="text-xs text-[#71717A] mt-0.5">Widget IA en haut du Dashboard avec recommandation quotidienne. Désactivé par défaut.</p>
              </div>
            </div>
            <Switch
              id="feat-coach-jour"
              checked={coachEnabled}
              onCheckedChange={v => update('feature_coach_du_jour_enabled', v ? 'true' : 'false')}
              data-testid="feat-coach-jour-switch"
            />
          </div>
        </CardContent>
      </Card>

      {/* Engine Settings */}
      <Card className="border border-[#E5E7EB] shadow-sm">
        <CardContent className="p-6 space-y-5">
          <div className="flex items-center gap-2 mb-2">
            <Zap className="w-4 h-4 text-[#F97316]" />
            <h3 className="text-sm font-semibold">Moteur de recherche</h3>
          </div>
          {Object.entries(ENGINE_SETTINGS).map(([key, config]) => (
            <div key={key}>
              <Label className="text-sm mb-1.5 block">{config.label}</Label>
              {config.type === 'number' ? (
                <Input type="number" value={settings[key] || ''} onChange={e => update(key, e.target.value)}
                  className="h-9 bg-white max-w-xs" data-testid={`setting-${key}`} />
              ) : config.type === 'select' ? (
                <Select value={settings[key] || ''} onValueChange={v => update(key, v)}>
                  <SelectTrigger className="h-9 bg-white max-w-xs" data-testid={`setting-${key}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {config.options.map(opt => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : null}
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Email Pattern Settings */}
      <Card className="border border-[#E5E7EB] shadow-sm">
        <CardContent className="p-6 space-y-5">
          <div className="flex items-center gap-2 mb-2">
            <Mail className="w-4 h-4 text-[#0055FF]" />
            <h3 className="text-sm font-semibold">Patterns emails professionnels</h3>
          </div>
          <p className="text-xs text-[#71717A]">
            Variables disponibles : <code className="bg-[#F3F4F6] px-1 rounded">{'{prenom}'}</code> <code className="bg-[#F3F4F6] px-1 rounded">{'{nom}'}</code> <code className="bg-[#F3F4F6] px-1 rounded">{'{sca}'}</code> (SCA Leclerc par département)
          </p>
          {Object.entries(EMAIL_SETTINGS).map(([key, config]) => (
            <div key={key}>
              <Label className="text-sm mb-1.5 block">{config.label}</Label>
              <Input value={settings[key] || ''} onChange={e => update(key, e.target.value)}
                placeholder={config.placeholder} className="h-9 bg-white font-mono text-sm" data-testid={`setting-${key}`} />
            </div>
          ))}
        </CardContent>
      </Card>

      <div className="sticky bottom-4 flex justify-end">
        <Button onClick={handleSave} disabled={saving} data-testid="save-settings-btn"
          className="h-11 px-6 gap-2 bg-[#F97316] hover:bg-[#EA580C] text-white shadow-lg">
          <Save className="w-4 h-4" /> {saving ? 'Enregistrement...' : 'Enregistrer les paramètres'}
        </Button>
      </div>
    </div>
  );
}

